"use client";

import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { FunctionArgs } from "convex/server";
import type { DiscoveryProduct } from "@/components/discovery/types";
import type { HeroLocation } from "@/components/home/context/location-context";
import { toDiscoveryProduct } from "@/components/listing/browse/marketplace-listing";

export const NEAR_YOU_REACH_MILES = 500;
const ROW_LIMIT = 8;

interface NearYouProductsArgs {
  location: HeroLocation | null;
  isSeeding: boolean;
  productTypeSlug?: string;
}

interface NearYouProducts {
  products: DiscoveryProduct[];
  isLoading: boolean;
  isNationwide: boolean;
}

function nearbyArgs(
  location: HeroLocation,
  productTypeSlug: string | undefined,
): FunctionArgs<typeof api.products.nearbyQueries.getSmartNearbyWithFreight> {
  return {
    buyerLat: location.lat,
    buyerLon: location.lng,
    buyerZip: location.postalCode,
    buyerState: location.state,
    initialRadiusMiles: NEAR_YOU_REACH_MILES,
    limit: ROW_LIMIT,
    inStock: true,
    intent: "sell",
    productTypeSlug,
  };
}

function nationwideArgs(
  productTypeSlug: string | undefined,
): FunctionArgs<typeof api.products.marketplaceQueries.listMarketplacePage> {
  return {
    page: 1,
    perPage: ROW_LIMIT,
    inStock: true,
    sortBy: "newest",
    intent: "sell",
    productTypeSlug,
  };
}

export function useNearYouProducts({
  location,
  isSeeding,
  productTypeSlug,
}: NearYouProductsArgs): NearYouProducts {
  const nearby = useQuery(
    api.products.nearbyQueries.getSmartNearbyWithFreight,
    location ? nearbyArgs(location, productTypeSlug) : "skip",
  );
  const hasNoneNearby = location !== null && nearby !== undefined && nearby.products.length === 0;
  const nationwide = useQuery(
    api.products.marketplaceQueries.listMarketplacePage,
    (location === null && !isSeeding) || hasNoneNearby ? nationwideArgs(productTypeSlug) : "skip",
  );

  if (location && !hasNoneNearby) {
    return {
      products: nearby?.products ?? [],
      isLoading: nearby === undefined,
      isNationwide: false,
    };
  }
  return {
    products: (nationwide?.listings ?? []).map(toDiscoveryProduct),
    isLoading: isSeeding || nationwide === undefined,
    isNationwide: true,
  };
}
